import { platformToast } from "$root/notification-system/toast";
import { minutes } from "$root/utils/time";
import type { LabComputeEvent } from "@eu.mdotm.api/portfolio-lab";
import { getDefaultStore, readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import {
	chartsComputing,
	computeOutcome,
	isBotCompute,
	LAB_COMPUTE_STOP_MS,
	type LabComputeOutcome,
	reportsRevisionAfterCompute,
	reportsScenarioAfterCompute,
	scenarioFallbackAfterCompute,
} from "./common";
import type { PortfolioLabContextProps } from "./ctx";
import { portfolioLabRepository } from "./repository";

/**
 * Finishes the column's pending compute with a `lab-compute` event: its own, or the final one of a
 * compute at the revision it waits on that another tab or SphereAI queued, which replaced it (the
 * backend keeps one compute per column). An event at a later revision means another writer moved the
 * column on and its compute replaced the column's, which will never report, so the column stops
 * waiting (`dropReplacedCompute`); not while a save of its own is in flight, which may be what moved
 * it. Returns `false`, changing nothing, for any other event: one not answered yet, one at an earlier
 * revision, a superseded one at the revision it waits on, a deleted column's, or one for a column
 * waiting on a compute SphereAI started, which its call's result finishes.
 */
export function applyComputeEvent(ctx: PortfolioLabContextProps, event: LabComputeEvent): boolean {
	const column = readAtom(ctx.investments$).find((i) => i.virtualUuid === event.uuid);
	const pending = column?.pendingCompute;
	const outcome = computeOutcome(event);
	if (column == null || pending == null || isBotCompute(pending) || outcome == null) {
		return false;
	}
	const replacedAtPendingRevision = outcome.status !== "SUPERSEDED" && event.compositionRevision === pending.revision;
	if (pending.computeId === event.computeId || replacedAtPendingRevision) {
		applyComputeOutcome(ctx, event.uuid, outcome, {
			requested: pending.scenarioId,
			reports: event.scenarioIdUsed ?? null,
		});
		return true;
	}
	if (column.pendingWrites === 0 && event.compositionRevision != null && event.compositionRevision > pending.revision) {
		dropReplacedCompute(ctx, event.uuid);
		return true;
	}
	return false;
}

/**
 * Stops the column waiting on a compute another writer's replaced, and tells the user its charts are
 * out of date, unless the user has already been told: its last save failed, which may have reached
 * the server and moved it, or its compute passed the {@link LAB_COMPUTE_STOP_MS} stop. The charts
 * are left as they are: they describe neither the composition on screen nor the server's.
 */
export function dropReplacedCompute(ctx: PortfolioLabContextProps, virtualUuid: string): void {
	const column = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
	if (column?.pendingCompute == null) {
		return;
	}
	writeAtom(ctx.investments$, (items) =>
		items.map((i) => (i.virtualUuid === virtualUuid ? { ...i, pendingCompute: null } : i)),
	);
	if (column.compositionSaveStatus === "error" || column.pendingCompute.timedOut) {
		return;
	}
	platformToast({
		icon: "Icon-full-alert",
		severity: "warning",
		children: `Charts for ${column.name} are out of date: it was changed in another tab or by SphereAI. Click Update Charts to recalculate them.`,
	});
}

/**
 * Applies a finished compute to its column: moves `reportsComputedAtRevision`, clears
 * `pendingCompute`, records or clears the scenario fallback, refetches the column's charts, and tells
 * the user when the compute failed (the column's charts are then empty and stay stale).
 *
 * `scenario.requested` is the one the column asked for: a fallback is recorded against it and its
 * ex-ante metrics refetch. `scenario.reports` is the one the reports cover, which an event or the
 * server's column names; a compute this tab queued covers the one it asked for.
 */
export function applyComputeOutcome(
	ctx: PortfolioLabContextProps,
	virtualUuid: string,
	outcome: LabComputeOutcome,
	scenario: { requested: string | null; reports: string | null },
): void {
	const column = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
	if (column == null) {
		return;
	}
	writeAtom(ctx.investments$, (items) =>
		items.map((i) =>
			i.virtualUuid === virtualUuid
				? {
						...i,
						reportsComputedAtRevision: reportsRevisionAfterCompute(i.reportsComputedAtRevision, outcome),
						scenarioFallback: scenarioFallbackAfterCompute(i.scenarioFallback, outcome, scenario.requested),
						reportsScenarioId: reportsScenarioAfterCompute(i.reportsScenarioId, outcome, scenario.reports),
						pendingCompute: null,
					}
				: i,
		),
	);
	invalidateColumnCharts(virtualUuid, scenario.requested);
	if (outcome.status === "FAILED") {
		platformToast({
			icon: "Icon-full-error",
			severity: "error",
			children: `Charts for ${column.name} could not be calculated. Click Update Charts to try again.`,
		});
	}
}

export function invalidateColumnCharts(virtualUuid: string, scenarioId: string | null) {
	portfolioLabRepository.invalidate(
		portfolioLabRepository.getVirtualInvestmentPerformance.getQueryConfig(virtualUuid).queryKey,
	);

	portfolioLabRepository.invalidate(portfolioLabRepository.portfolioMetrics.getQueryConfig(virtualUuid, []).queryKey);

	portfolioLabRepository.invalidate(
		portfolioLabRepository.portfolioMetricsBaseline.getQueryConfig(virtualUuid, []).queryKey,
	);

	// Ex-ante scenario metrics are keyed by (virtualUuid, scenarioId); after a compute bound
	// to a scenario, the already-mounted queries for that scenario must refetch. Empty concepts make
	// the key a prefix, so every concept set for this investment+scenario is invalidated.
	if (scenarioId != null) {
		portfolioLabRepository.invalidate(
			portfolioLabRepository.getExAnteScenarioMetrics.getQueryConfig(virtualUuid, scenarioId, []).queryKey,
		);
		portfolioLabRepository.invalidate(
			portfolioLabRepository.getExAnteScenarioMetricsBaseline.getQueryConfig(virtualUuid, scenarioId, []).queryKey,
		);
	}
}

/** Resolves once none of `virtualUuids` is computing; a column no longer in the lab counts as done. */
export function untilComputed(ctx: PortfolioLabContextProps, virtualUuids: string[]): Promise<void> {
	const wanted = new Set(virtualUuids);
	const done = () => !readAtom(ctx.investments$).some((i) => wanted.has(i.virtualUuid) && chartsComputing(i));
	if (done()) {
		return Promise.resolve();
	}
	return new Promise((resolve) => {
		const unsubscribe = getDefaultStore().sub(ctx.investments$, () => {
			if (done()) {
				unsubscribe();
				resolve();
			}
		});
	});
}

/** Tells the user a queued compute gave no result before the {@link LAB_COMPUTE_STOP_MS} stop. */
export function toastComputeStopped(columnName: string): void {
	platformToast({
		icon: "Icon-full-error",
		severity: "error",
		children: `No result for ${columnName} after ${LAB_COMPUTE_STOP_MS / minutes(1)} minutes. Click Update Charts to try again.`,
	});
}
